import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/ProjectDetailPage.scss';
import { 
    FaCalendarAlt, FaChevronDown, FaChevronRight, FaSitemap, FaEye, FaPlus, FaEdit, FaTrashAlt, 
    FaSpinner, FaExclamationTriangle, FaArrowLeft, FaUser, FaFlagCheckered, FaTasks, FaListAlt,
    FaColumns, FaChartLine, FaFlag, FaCog, FaUndo, FaGripVertical, FaInfoCircle, FaBell, FaCheckCircle,
    FaCheck, FaTimes, FaComments, FaPaperclip, FaCheckSquare, FaUserCircle, FaUpload,
    FaFolderOpen, FaFile, FaInbox, FaClipboardList, FaGem, FaCalendar, FaExclamation, FaStar,
    FaTrophy, FaRocket, FaBullseye, FaCrown, FaProjectDiagram, FaCircle, FaSquare
} from 'react-icons/fa';

/**
 * Import SignalR hook for real-time updates
 */
import useActionItemSignalR from '@/hooks/useActionItemSignalR';

/**
 * Import Gantt Chart component
 */
import GanttChart from './components/GanttChart';
import KanbanSwimlane from './components/KanbanSwimlane';
import PageHeader from '@/components/Common/PageHeader';
import CommentInputSection from '@/pages/ActionItems/components/CommentInputSection';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import * as Sentry from '@sentry/react';

/**
 * Available status options for tasks
 */
const TASK_STATUSES = ['Open', 'In Progress', 'Pending', 'Closed', 'Completed'];

/**
 * Available priority options for tasks
 */
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

/**
 * Available milestone icons - maps icon keys to react-icons components
 */
const MILESTONE_ICONS = [
    { value: 'flag', label: 'Flag', Icon: FaFlag },
    { value: 'star', label: 'Star', Icon: FaStar },
    { value: 'trophy', label: 'Trophy', Icon: FaTrophy },
    { value: 'check-circle', label: 'Check', Icon: FaCheckCircle },
    { value: 'rocket', label: 'Rocket', Icon: FaRocket },
    { value: 'bullseye', label: 'Target', Icon: FaBullseye },
    { value: 'gem', label: 'Gem', Icon: FaGem },
    { value: 'crown', label: 'Crown', Icon: FaCrown }
];

/**
 * Milestone color presets
 */
const MILESTONE_COLORS = ['#2ecc71', '#3498db', '#9b59b6', '#f39c12', '#e74c3c', '#1abc9c', '#34495e'];

/**
 * Format date as "Day Month Year" (e.g., "2 December 2025")
 * @param {string|Date} dateValue - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
    });
};

/**
 * Format date for input field (YYYY-MM-DD)
 */
const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    return date.toISOString().split('T')[0];
};

/**
 * Custom DateInput component that displays dates in "day month year" format
 */
const DateInput = ({ id, name, value, onChange, placeholder = 'Select date' }) => {
    const displayValue = value ? formatDate(value) : '';
    
    return (
        <div className="date-input-wrapper">
            <input
                type="date"
                id={id}
                name={name}
                value={value}
                onChange={onChange}
                className="date-input-native"
            />
            <div className="date-input-display">
                <span className={displayValue ? '' : 'placeholder'}>
                    {displayValue || placeholder}
                </span>
                <FaCalendarAlt />
            </div>
        </div>
    );
};

/**
 * Default Kanban columns (used when project has no custom columns)
 */
const DEFAULT_KANBAN_COLUMNS = [
    { id: 'Open', name: 'Open', statusValue: 'Open', color: '#f39c12', isCompletedColumn: false },
    { id: 'In Progress', name: 'In Progress', statusValue: 'In Progress', color: '#3498db', isCompletedColumn: false },
    { id: 'Pending', name: 'Pending', statusValue: 'Pending', color: '#9b59b6', isCompletedColumn: false },
    { id: 'Completed', name: 'Completed', statusValue: 'Completed', color: '#27ae60', isCompletedColumn: true },
    { id: 'Closed', name: 'Closed', statusValue: 'Closed', color: '#7f8c8d', isCompletedColumn: true }
];

const ProjectDetailPage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { apiFetch, user } = useAuth();
    
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [stats, setStats] = useState(null);
    const [milestones, setMilestones] = useState([]);
    const [kanbanColumns, setKanbanColumns] = useState(DEFAULT_KANBAN_COLUMNS);
    const [usingDefaultColumns, setUsingDefaultColumns] = useState(true);
    // Persist active tab per-project so users return to the same view they left
    const [activeTab, setActiveTab] = useState(() => {
        try {
            return localStorage.getItem(`projectTab:${projectId}`) || 'tasks';
        } catch { return 'tasks'; }
    });
    useEffect(() => {
        try { localStorage.setItem(`projectTab:${projectId}`, activeTab); } catch { /* ignore storage errors */ }
    }, [activeTab, projectId]);
    const [draggedTask, setDraggedTask] = useState(null);
    
    // Template state for dynamic forms
    const [taskTemplate, setTaskTemplate] = useState(null);
    const [subtaskTemplate, setSubtaskTemplate] = useState(null);
    const [milestoneTemplate, setMilestoneTemplate] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    
    // Modal states
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const [editingColumn, setEditingColumn] = useState(null);
    const [parentTaskId, setParentTaskId] = useState(null);
    
    // Task detail modal state (for comments, files, subtasks)
    const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskDetailTab, setTaskDetailTab] = useState('details'); // 'details', 'subtasks', 'comments', 'files', 'checklist'
    const [taskComments, setTaskComments] = useState([]);
    const [taskFiles, setTaskFiles] = useState([]);
    const [taskChecklist, setTaskChecklist] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [newChecklistItem, setNewChecklistItem] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const fileInputRef = useRef(null);
    
    // Dynamic form fields from template (for custom fields)
    const [dynamicFormFields, setDynamicFormFields] = useState({});
    
    // Task form state
    const [taskForm, setTaskForm] = useState({
        title: '',
        description: '',
        status: 'Open',
        priority: 'Medium',
        assignedToUserId: '',
        department: '',
        startDate: '',
        expectedCompletionDate: '',
        milestoneId: '' // Added milestone selection
    });

    // Column form state
    const [columnForm, setColumnForm] = useState({
        name: '',
        statusValue: '',
        color: '#3498db',
        isCompletedColumn: false,
        wipLimit: 0
    });

    // Milestone form state
    const [milestoneForm, setMilestoneForm] = useState({
        name: '',
        description: '',
        targetDate: '',
        notifyOnAchievement: true,
        additionalNotifyEmails: '',
        notifyStakeholders: false,
        color: '#2ecc71',
        icon: 'flag',
        isCritical: false,
        progressWeight: ''
    });
    
    // Expanded tasks (for subtask visibility)
    const [expandedTasks, setExpandedTasks] = useState(new Set());

    /**
     * Resolve a milestone icon component from its stored key.
     * Falls back to FaFlag for unknown / legacy values (e.g. 'fa-flag' strings).
     */
    const getMilestoneIcon = (iconKey) => {
        const match = MILESTONE_ICONS.find(i => i.value === iconKey);
        return match ? match.Icon : FaFlag;
    };

    // Auth context uses 'userID' (from login), while User API returns 'id' - they're different data sources
    const userId = user?.userID || parseInt(localStorage.getItem('userID') || '0', 10);

    /**
     * Global Escape-to-close handler for all modals on this page. Closes the
     * topmost open modal only, matching user expectation in stacked dialogs.
     */
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (showTaskDetailModal) { setShowTaskDetailModal(false); return; }
            if (showTaskModal) { setShowTaskModal(false); return; }
            if (showMilestoneModal) { setShowMilestoneModal(false); return; }
            if (showColumnModal) { setShowColumnModal(false); return; }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [showTaskDetailModal, showTaskModal, showMilestoneModal, showColumnModal]);

    /**
     * Get a display-friendly name for a user object
     */
    const getUserDisplay = (userObj) => {
        if (!userObj) return '';
        const first = userObj.firstName || userObj.name || '';
        const last = userObj.lastName || userObj.surname || '';
        const composed = `${first} ${last}`.trim();
        return composed || userObj.displayName || userObj.email || (userObj.userID ?? userObj.id) || '';
    };

    /**
     * SignalR hook for real-time project task updates.
     * Handles status changes, assignment changes, and priority updates in real-time
     * for the Kanban board without requiring page refresh or polling.
     */
    const handleProjectTaskUpdate = useCallback((update) => {
        console.log('[SignalR Project] Received update:', update);
        
        // Check for actionItemId (lowercase from SignalR)
        const actionItemId = update?.actionItemId || update?.ActionItemId;
        
        if (!actionItemId) {
            console.error('[SignalR Project] Invalid update - missing actionItemId:', update);
            return;
        }
        
        // Update the specific task in our local state
        setTasks(prevTasks => {
            // Ensure we're comparing IDs correctly (handle both number and string)
            const updateId = parseInt(actionItemId, 10);
            
            if (isNaN(updateId)) {
                console.error('[SignalR Project] Invalid actionItemId - cannot parse:', actionItemId);
                return prevTasks;
            }
            
            let taskFound = false;
            const updated = prevTasks.map(task => {
                // Compare IDs as numbers to handle type mismatches
                if (parseInt(task.id, 10) === updateId) {
                    taskFound = true;
                    console.log('[SignalR Project] Updating task:', task.id, 'Type:', update.Type || update.type);
                    
                    // Get update type and values (handle both cases)
                    const updateType = update.Type || update.type;
                    const newStatus = update.NewStatus || update.newStatus;
                    const newAssignedUserId = update.NewAssignedUserId ?? update.newAssignedUserId;
                    const newPriority = update.Data?.NewPriority || update.data?.newPriority;
                    
                    // Apply the update based on type
                    if (updateType === 'StatusChange' && newStatus) {
                        console.log('[SignalR Project] Status change:', task.status, '->', newStatus);
                        return { ...task, status: newStatus };
                    }
                    if (updateType === 'AssignmentChange' && newAssignedUserId !== undefined) {
                        console.log('[SignalR Project] Assignment change:', task.assignedToUserId, '->', newAssignedUserId);
                        return { ...task, assignedToUserId: newAssignedUserId };
                    }
                    if (updateType === 'PriorityChange' && newPriority) {
                        console.log('[SignalR Project] Priority change:', task.priority, '->', newPriority);
                        return { ...task, priority: newPriority };
                    }
                }
                return task;
            });
            
            if (taskFound) {
                console.log('[SignalR Project] State updated, tasks count:', updated.length);
                // Return a new array to ensure React detects the change
                return [...updated];
            } else {
                console.warn('[SignalR Project] Warning: Task not found with ID:', updateId);
                return prevTasks;
            }
        });
    }, []);

    const { isConnected: signalRConnected, joinProjectGroup, leaveProjectGroup } = useActionItemSignalR({
        onProjectTaskUpdated: handleProjectTaskUpdate,
        onStatusChange: handleProjectTaskUpdate,
        onAssignmentChange: handleProjectTaskUpdate,
        enabled: true,
    });

    // Join project group when projectId changes for targeted updates
    useEffect(() => {
        if (projectId && signalRConnected) {
            joinProjectGroup(parseInt(projectId, 10));
        }
        return () => {
            if (projectId && signalRConnected) {
                leaveProjectGroup(parseInt(projectId, 10));
            }
        };
    }, [projectId, signalRConnected, joinProjectGroup, leaveProjectGroup]);

    /**
     * Fetch project details and tasks
     */
    const fetchProject = useCallback(async () => {
        if (!projectId) return;
        
        setLoading(true);
        try {
            const response = await apiFetch(`/api/Projects/${projectId}`);
            const data = await response.json();
            
            if (data.success) {
                setProject(data.data.project);
                setTasks(data.data.tasks || []);
            } else {
                toast.error(data.message || 'Failed to load project');
                navigate('/projects');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching project:', error);
            toast.error('Failed to load project');
            navigate('/projects');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, projectId, navigate]);

    /**
     * Fetch project templates for Tasks, Subtasks, and Milestones
     */
    const fetchProjectTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        try {
            // Fetch template assignments for Project contexts
            const contexts = ['ProjectTask', 'ProjectSubtask', 'ProjectMilestone'];
            
            for (const contextKey of contexts) {
                try {
                    const assignmentResponse = await apiFetch(`/api/ModuleTemplateAssignments/Projects/${contextKey}`);
                    const assignmentData = await assignmentResponse.json();
                    
                    if (assignmentResponse.ok && assignmentData.success && assignmentData.data?.assignedTemplateKey) {
                        // Fetch the full template schema
                        const templateResponse = await apiFetch(`/api/Templates/${assignmentData.data.assignedTemplateKey}`);
                        const templateData = await templateResponse.json();
                        
                        if (templateResponse.ok && templateData.success && templateData.data) {
                            if (contextKey === 'ProjectTask') {
                                setTaskTemplate(templateData.data);
                            } else if (contextKey === 'ProjectSubtask') {
                                setSubtaskTemplate(templateData.data);
                            } else if (contextKey === 'ProjectMilestone') {
                                setMilestoneTemplate(templateData.data);
                            }
                        }
                    }
                } catch (error) {
                    Sentry.captureException(error);
                    console.debug(`No template assigned for ${contextKey}`, error);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching project templates:', error);
        } finally {
            setLoadingTemplates(false);
        }
    }, [apiFetch]);

    /**
     * Parse template schema to get field definitions
     */
    const getTemplateFields = (template) => {
        if (!template?.schema) return [];
        try {
            const schema = typeof template.schema === 'string' 
                ? JSON.parse(template.schema) 
                : template.schema;
            return schema.fields || [];
        } catch (catchError) {
            Sentry.captureException(catchError);
            return [];
        }
    };

    /**
     * Get the active template based on whether we're creating a task or subtask
     */
    const getActiveTemplate = () => {
        if (parentTaskId) {
            return subtaskTemplate || taskTemplate;
        }
        return taskTemplate;
    };

    /**
     * Render a dynamic field based on template field definition
     */
    const renderTemplateField = (field, value, onChange) => {
        const fieldId = `dynamic-${field.name}`;
        
        switch (field.type) {
            case 'text':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="text"
                            id={fieldId}
                            value={value || ''}
                            onChange={(e) => onChange(field.name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'textarea':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <textarea
                            id={fieldId}
                            value={value || ''}
                            onChange={(e) => onChange(field.name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            rows={field.rows || 3}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'select':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <select
                            id={fieldId}
                            value={value || ''}
                            onChange={(e) => onChange(field.name, e.target.value)}
                            required={field.required}
                        >
                            <option value="">Select {field.label}</option>
                            {(field.options || []).map(opt => (
                                <option key={opt.value || opt} value={opt.value || opt}>
                                    {opt.label || opt}
                                </option>
                            ))}

                        </select>
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'number':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="number"
                            id={fieldId}
                            value={value || ''}    onChange={(e) => onChange(field.name, e.target.value)}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'date':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="date"
                            id={fieldId}
                            value={value || ''}    onChange={(e) => onChange(field.name, e.target.value)}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'checkbox':
                return (
                    <div key={field.name} className="form-group form-check-group">
                        <label className="form-check">
                            <input
                                type="checkbox"
                                id={fieldId}
                                checked={value || false}
                                onChange={(e) => onChange(field.name, e.target.checked)}
                            />
                            <span>{field.label}</span>
                        </label>
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'email':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="email"
                            id={fieldId}
                            value={value || ''}    onChange={(e) => onChange(field.name, e.target.value)}
                            placeholder={field.placeholder || ''}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            case 'url':
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="url"
                            id={fieldId}
                            value={value || ''}    onChange={(e) => onChange(field.name, e.target.value)}
                            placeholder={field.placeholder || 'https://'}
                            required={field.required}
                        />
                        {field.helpText && <small className="form-hint">{field.helpText}</small>}
                    </div>
                );
            
            default:
                return (
                    <div key={field.name} className="form-group">
                        <label htmlFor={fieldId}>{field.label}{field.required && ' *'}</label>
                        <input
                            type="text"
                            id={fieldId}
                            value={value || ''}    onChange={(e) => onChange(field.name, e.target.value)}
                            required={field.required}
                        />
                    </div>
                );
        }
    };

    /**
     * Handle dynamic field value change
     */
    const handleDynamicFieldChange = (fieldName, value) => {
        setDynamicFormFields(prev => ({
            ...prev,
            [fieldName]: value
        }));
    };

    /**
     * Fetch project statistics
     */
    const fetchStats = useCallback(async () => {
        if (!projectId) return;
        
        try {
            const response = await apiFetch(`/api/Projects/${projectId}/stats`);
            const data = await response.json();
            
            if (data.success) {
                setStats(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching stats:', error);
        }
    }, [apiFetch, projectId]);

    /**
     * Fetch users for assignment dropdown
     */
    const fetchUsers = useCallback(async () => {
        try {
            const response = await apiFetch('/api/User/all');
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                setUsers(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching users:', error);
        }
    }, [apiFetch]);

    /**
     * Fetch departments
     */
    const fetchDepartments = useCallback(async () => {
        try {
            const response = await apiFetch('/api/Department');
            const data = await response.json();
            if (data.success && data.data) {
                const deptNames = data.data
                    .filter(d => d && d.isActive && d.departmentName)
                    .map(d => d.departmentName);
                setDepartments(deptNames);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching departments:', error);
        }
    }, [apiFetch]);

    /**
     * Fetch milestones for the project
     */
    const fetchMilestones = useCallback(async () => {
        if (!projectId) return;
        
        try {
            const response = await apiFetch(`/api/Projects/${projectId}/Milestones`);
            const data = await response.json();
            
            if (data.success) {
                setMilestones(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching milestones:', error);
        }
    }, [apiFetch, projectId]);

    /**
     * Fetch Kanban columns for the project
     */
    const fetchKanbanColumns = useCallback(async () => {
        if (!projectId) return;
        
        try {
            const response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns`);
            const data = await response.json();
            
            if (data.success) {
                const columns = data.data.map(col => ({
                    id: col.statusValue,
                    columnId: col.columnId,
                    name: col.name,
                    statusValue: col.statusValue,
                    color: col.color || '#3498db',
                    isCompletedColumn: col.isCompletedColumn,
                    wipLimit: col.wipLimit,
                    isCustom: col.isCustom
                }));
                setKanbanColumns(columns);
                setUsingDefaultColumns(data.usingDefaults);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching Kanban columns:', error);
            setKanbanColumns(DEFAULT_KANBAN_COLUMNS);
            setUsingDefaultColumns(true);
        }
    }, [apiFetch, projectId]);

    useEffect(() => {
        fetchProject();
        fetchStats();
        fetchUsers();
        fetchDepartments();
        fetchMilestones();
        fetchKanbanColumns();
        fetchProjectTemplates();
    }, [fetchProject, fetchStats, fetchUsers, fetchDepartments, fetchMilestones, fetchKanbanColumns, fetchProjectTemplates]);

    /**
     * Polling effect to automatically refresh project data.
     * Reduced frequency since SignalR provides real-time updates for task changes.
     * Acts as a fallback to ensure data consistency if SignalR connection is lost.
     * Pauses polling when the browser tab is not visible to save resources.
     */
    useEffect(() => {
        const POLLING_INTERVAL = 60000; // 60 seconds (reduced from 30s since SignalR handles real-time)
        let pollInterval;
        
        /**
         * Silently fetches project data without showing loading state.
         * Used for background polling to update data in real-time.
         */
        const pollProjectData = async () => {
            if (!projectId) return;
            
            try {
                // Fetch project and tasks
                const projectResponse = await apiFetch(`/api/Projects/${projectId}`);
                const projectData = await projectResponse.json();
                
                if (projectData.success) {
                    setProject(projectData.data.project);
                    setTasks(projectData.data.tasks || []);
                }

                // Fetch stats
                const statsResponse = await apiFetch(`/api/Projects/${projectId}/stats`);
                const statsData = await statsResponse.json();
                if (statsData.success) {
                    setStats(statsData.data);
                }

                // Fetch milestones
                const milestonesResponse = await apiFetch(`/api/Projects/${projectId}/Milestones`);
                const milestonesData = await milestonesResponse.json();
                if (milestonesData.success) {
                    setMilestones(milestonesData.data || []);
                }
            } catch (e) {
                Sentry.captureException(e);
                // Silently fail on polling errors to avoid spamming the user
                console.debug("Polling error:", e);
            }
        };
        
        /**
         * Handle visibility change - pause polling when tab is hidden.
         */
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden, clear the interval
                if (pollInterval) {
                    clearInterval(pollInterval);
                    pollInterval = null;
                }
            } else {
                // Page is visible again, restart polling
                if (!pollInterval) {
                    pollProjectData(); // Fetch immediately when becoming visible
                    pollInterval = setInterval(pollProjectData, POLLING_INTERVAL);
                }
            }
        };
        
        // Start polling
        pollInterval = setInterval(pollProjectData, POLLING_INTERVAL);
        
        // Listen for visibility changes
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup on unmount
        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [apiFetch, projectId]);

    /**
     * Get user name by ID
     */
    const getUserName = (userId) => {
        const foundUser = users.find(u => u.id === userId);
        return foundUser ? foundUser.name : 'Unassigned';
    };

    /**
     * Toggle task expansion (for subtasks)
     */
    const toggleTaskExpanded = (taskId) => {
        setExpandedTasks(prev => {
            const next = new Set(prev);
            if (next.has(taskId)) {
                next.delete(taskId);
            } else {
                next.add(taskId);
            }
            return next;
        });
    };

    /**
     * Open task detail modal with comments, files, subtasks tabs
     */
    const handleOpenTaskDetail = async (task) => {
        setSelectedTask(task);
        setTaskDetailTab('details');
        setShowTaskDetailModal(true);
        
        // Fetch task comments/logs
        try {
            const response = await apiFetch(`/api/ActionItems/${task.id}/logs`);
            const data = await response.json();
            if (data.success) {
                // Filter for comments only
                const comments = (data.data || []).filter(log => 
                    log.actionType === 'Comment' || log.actionType === 'comment'
                );
                setTaskComments(comments);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching task comments:', error);
            setTaskComments([]);
        }
        
        // Fetch task files
        try {
            const response = await apiFetch(`/api/ActionItems/action-items/${task.id}/attachments`);
            const data = await response.json();
            if (data.success) {
                setTaskFiles(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching task files:', error);
            setTaskFiles([]);
        }
        
        // Fetch task checklist (if exists)
        try {
            const response = await apiFetch(`/api/ActionItems/${task.id}/checklist`);
            const data = await response.json();
            if (data.success) {
                setTaskChecklist(data.data || []);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.debug('Error fetching task checklist:', error);
            setTaskChecklist([]);
        }
    };

    /**
     * Close task detail modal
     */
    const handleCloseTaskDetail = () => {
        setShowTaskDetailModal(false);
        setSelectedTask(null);
        setTaskComments([]);
        setTaskFiles([]);
        setTaskChecklist([]);
        setNewComment('');
        setNewChecklistItem('');
    };

    /**
     * Add a comment to the task
     */
    const handleAddComment = async () => {
        if (!newComment.trim() || !selectedTask) return;

        setIsSubmittingComment(true);
        try {
            const response = await apiFetch(`/api/ActionItems/action-items/${selectedTask.id}/comment`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: userId,
                    comment: newComment.trim()
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Comment added');
                setNewComment('');
                // Refresh comments
                const logsResponse = await apiFetch(`/api/ActionItems/${selectedTask.id}/logs`);
                const logsData = await logsResponse.json();
                if (logsData.success) {
                    const comments = (logsData.data || []).filter(log => 
                        log.actionType === 'Comment' || log.actionType === 'comment'
                    );
                    setTaskComments(comments);
                }
            } else {
                toast.error(data.message || 'Failed to add comment');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error adding comment:', error);
            toast.error('Failed to add comment');
        } finally {
            setIsSubmittingComment(false);
        }
    };

    /**
     * Handle file upload
     */
    const handleFileUpload = async (event) => {
        const files = event.target.files;
        if (!files || files.length === 0 || !selectedTask) return;

        try {
            let uploadedCount = 0;
            // Upload each file individually to match backend single-file endpoint
            for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);
                formData.append('userId', userId.toString());

                const response = await apiFetch(`/api/ActionItems/action-items/${selectedTask.id}/attachments`, {
                    method: 'POST',
                    body: formData
                });

                const data = await response.json();
                if (data.success) {
                    uploadedCount++;
                } else {
                    toast.error(data.message || `Failed to upload ${files[i].name}`);
                }
            }

            if (uploadedCount > 0) {
                toast.success(`${uploadedCount} file(s) uploaded`);
                // Refresh files list
                const filesResponse = await apiFetch(`/api/ActionItems/action-items/${selectedTask.id}/attachments`);
                const filesData = await filesResponse.json();
                if (filesData.success) {
                    setTaskFiles(filesData.data || []);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error uploading file:', error);
            toast.error('Failed to upload file');
        }

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Add checklist item
     */
    const handleAddChecklistItem = async () => {
        if (!newChecklistItem.trim() || !selectedTask) return;
        
        try {
            const response = await apiFetch(`/api/ActionItems/${selectedTask.id}/checklist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: newChecklistItem.trim(),
                    userId: userId
                })
            });
            
            const data = await response.json();
            if (data.success) {
                toast.success('Checklist item added');
                setNewChecklistItem('');
                // Refresh checklist
                const checklistResponse = await apiFetch(`/api/ActionItems/${selectedTask.id}/checklist`);
                const checklistData = await checklistResponse.json();
                if (checklistData.success) {
                    setTaskChecklist(checklistData.data || []);
                }
            } else {
                toast.error(data.message || 'Failed to add checklist item');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error adding checklist item:', error);
            toast.error('Failed to add checklist item');
        }
    };

    /**
     * Toggle checklist item completion
     */
    const handleToggleChecklistItem = async (itemId, currentCompleted) => {
        if (!selectedTask) return;
        
        try {
            const response = await apiFetch(`/api/ActionItems/${selectedTask.id}/checklist/${itemId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    completed: !currentCompleted,
                    userId: userId
                })
            });
            
            const data = await response.json();
            if (data.success) {
                // Refresh checklist
                const checklistResponse = await apiFetch(`/api/ActionItems/${selectedTask.id}/checklist`);
                const checklistData = await checklistResponse.json();
                if (checklistData.success) {
                    setTaskChecklist(checklistData.data || []);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error toggling checklist item:', error);
        }
    };

    /**
     * Open modal for creating new task
     */
    const handleCreateTask = (parentId = null, milestoneIdForTask = null) => {
        setEditingTask(null);
        setParentTaskId(parentId);
        setTaskForm({
            title: '',
            description: '',
            status: 'Open',
            priority: 'Medium',
            assignedToUserId: '',
            department: project?.department || '',
            startDate: formatDateForInput(new Date()),
            expectedCompletionDate: '',
            milestoneId: milestoneIdForTask?.toString() || ''
        });
        setDynamicFormFields({});
        setShowTaskModal(true);
    };

    /**
     * Open modal for editing task
     */
    const handleEditTask = (task) => {
        setEditingTask(task);
        setParentTaskId(task.parentActionItemId);
        setTaskForm({
            title: task.title || '',
            description: task.description || '',
            status: task.status || 'Open',
            priority: task.priority || 'Medium',
            assignedToUserId: task.assignedToUserID?.toString() || '',
            department: task.department || '',
            startDate: formatDateForInput(task.startDate),
            expectedCompletionDate: formatDateForInput(task.expectedCompletionDate),
            milestoneId: task.milestoneId?.toString() || ''
        });
        // Load custom fields if available
        if (task.customFields) {
            try {
                const customFields = typeof task.customFields === 'string' 
                    ? JSON.parse(task.customFields) 
                    : task.customFields;
                setDynamicFormFields(customFields);
            } catch (catchError) {
                Sentry.captureException(catchError);
                setDynamicFormFields({});
            }
        } else {
            setDynamicFormFields({});
        }
        setShowTaskModal(true);
    };

    /**
     * Handle task form input changes
     */
    const handleTaskInputChange = (e) => {
        const { name, value } = e.target;
        setTaskForm(prev => ({ ...prev, [name]: value }));
    };

    /**
     * Save task (create or update)
     */
    const handleSaveTask = async (e) => {
        e.preventDefault();
        
        if (!taskForm.title.trim()) {
            toast.error('Task title is required');
            return;
        }

        // Get the active template to determine templateKey
        const activeTemplate = getActiveTemplate();
        const templateKey = activeTemplate?.templateKey || null;

        try {
            if (editingTask) {
                // Update existing task via ActionItems API
                const response = await apiFetch(`/api/ActionItems/${editingTask.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: taskForm.title,
                        description: taskForm.description,
                        status: taskForm.status,
                        priority: taskForm.priority,
                        assignedToUserID: taskForm.assignedToUserId ? parseInt(taskForm.assignedToUserId) : null,
                        department: taskForm.department,
                        startDate: taskForm.startDate ? new Date(taskForm.startDate).toISOString() : null,
                        expectedCompletionDate: taskForm.expectedCompletionDate ? new Date(taskForm.expectedCompletionDate).toISOString() : null,
                        milestoneId: taskForm.milestoneId ? parseInt(taskForm.milestoneId) : null,
                        templateKey: templateKey,
                        customFields: Object.keys(dynamicFormFields).length > 0 ? JSON.stringify(dynamicFormFields) : null,
                        userId
                    })
                });
                
                const data = await response.json();
                if (data.success) {
                    toast.success('Task updated successfully');
                    setShowTaskModal(false);
                    setDynamicFormFields({});
                    fetchProject();
                    fetchStats();
                } else {
                    toast.error(data.message || 'Failed to update task');
                }
            } else {
                // Create new task
                const response = await apiFetch(`/api/Projects/${projectId}/tasks/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        title: taskForm.title,
                        description: taskForm.description,
                        status: taskForm.status,
                        priority: taskForm.priority,
                        assignedToUserId: taskForm.assignedToUserId ? parseInt(taskForm.assignedToUserId) : null,
                        department: taskForm.department,
                        startDate: taskForm.startDate ? new Date(taskForm.startDate).toISOString() : null,
                        expectedCompletionDate: taskForm.expectedCompletionDate ? new Date(taskForm.expectedCompletionDate).toISOString() : null,
                        milestoneId: taskForm.milestoneId ? parseInt(taskForm.milestoneId) : null,
                        parentActionItemId: parentTaskId,
                        templateKey: templateKey,
                        customFields: Object.keys(dynamicFormFields).length > 0 ? JSON.stringify(dynamicFormFields) : null,
                        userId
                    })
                });

                const data = await response.json();
                if (data.success) {
                    toast.success('Task created successfully');
                    setShowTaskModal(false);
                    setDynamicFormFields({});
                    fetchProject();
                    fetchStats();
                } else {
                    toast.error(data.message || 'Failed to create task');
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving task:', error);
            toast.error('Failed to save task');
        }
    };

    /**
     * Update task status quickly
     */
    const handleQuickStatusUpdate = async (taskId, newStatus) => {
        try {
            const response = await apiFetch(`/api/ActionItems/${taskId}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus, userId })
            });

            const data = await response.json();
            if (data.success) {
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to update status');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    /**
     * Remove task from project
     */
    const handleRemoveTask = async (taskId) => {
        if (!window.confirm('Remove this task from the project?')) return;

        try {
            const response = await apiFetch(`/api/Projects/${projectId}/tasks/${taskId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Task removed from project');
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to remove task');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error removing task:', error);
            toast.error('Failed to remove task');
        }
    };

    // ==================== MILESTONE HANDLERS ====================

    /**
     * Save milestone (create or update)
     */
    const handleSaveMilestone = async () => {
        if (!milestoneForm.name.trim()) {
            toast.error('Milestone name is required');
            return;
        }

        try {
            const payload = {
                name: milestoneForm.name,
                description: milestoneForm.description || null,
                targetDate: milestoneForm.targetDate || null,
                notifyOnAchievement: milestoneForm.notifyOnAchievement,
                additionalNotifyEmails: milestoneForm.additionalNotifyEmails || null,
                notifyStakeholders: milestoneForm.notifyStakeholders,
                color: milestoneForm.color,
                icon: milestoneForm.icon,
                isCritical: milestoneForm.isCritical,
                progressWeight: milestoneForm.progressWeight ? parseInt(milestoneForm.progressWeight, 10) : null,
                templateKey: milestoneTemplate?.templateKey || null,
                customFields: Object.keys(dynamicFormFields).length > 0 ? JSON.stringify(dynamicFormFields) : null,
                userId
            };

            let response;
            if (editingMilestone) {
                response = await apiFetch(`/api/Projects/${projectId}/Milestones/${editingMilestone.milestoneId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await apiFetch(`/api/Projects/${projectId}/Milestones`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();
            if (data.success) {
                toast.success(editingMilestone ? 'Milestone updated' : 'Milestone created');
                setShowMilestoneModal(false);
                setDynamicFormFields({});
                fetchMilestones();
            } else {
                toast.error(data.message || 'Failed to save milestone');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving milestone:', error);
            toast.error('Failed to save milestone');
        }
    };

    // ==================== END MILESTONE HANDLERS ====================

    /**
     * Open milestone modal for editing
     */
    const handleEditMilestone = (milestone) => {
        setEditingMilestone(milestone);
        setMilestoneForm({
            name: milestone.name || '',
            description: milestone.description || '',
            targetDate: formatDateForInput(milestone.targetDate),
            notifyOnAchievement: milestone.notifyOnAchievement ?? true,
            additionalNotifyEmails: milestone.additionalNotifyEmails || '',
            notifyStakeholders: milestone.notifyStakeholders ?? false,
            color: milestone.color || '#2ecc71',
            icon: milestone.icon || 'flag',
            isCritical: milestone.isCritical ?? false,
            progressWeight: milestone.progressWeight ?? ''
        });
        setShowMilestoneModal(true);
    };

    /**
     * Open milestone modal for creating
     */
    const handleCreateMilestone = () => {
        setEditingMilestone(null);
        setMilestoneForm({
            name: '',
            description: '',
            targetDate: '',
            notifyOnAchievement: true,
            additionalNotifyEmails: '',
            notifyStakeholders: false,
            color: '#2ecc71',
            icon: 'flag',
            isCritical: false,
            progressWeight: ''
        });
        setDynamicFormFields({});
        setShowMilestoneModal(true);
    };

    /**
     * Delete a milestone
     */
    const handleDeleteMilestone = async (milestoneId) => {
        if (!window.confirm('Are you sure you want to delete this milestone? Tasks assigned to it will become unassigned.')) return;

        try {
            const response = await apiFetch(`/api/Projects/${projectId}/Milestones/${milestoneId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Milestone deleted');
                fetchMilestones();
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to delete milestone');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting milestone:', error);
            toast.error('Failed to delete milestone');
        }
    };

    // ==================== KANBAN HANDLERS ====================

    /**
     * Handle drag start for Kanban cards
     */
    const handleDragStart = (e, task, isSubtask = false) => {
        setDraggedTask({ ...task, isSubtask });
        e.dataTransfer.effectAllowed = 'move';
    };

    /**
     * Handle drag over for Kanban columns
     */
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    /**
     * Handle drop on Kanban column (status change)
     */
    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!draggedTask) {
            setDraggedTask(null);
            return;
        }

        const statusChanged = draggedTask.status !== newStatus;
        
        // If status changed, update status
        if (statusChanged) {
            try {
                const response = await apiFetch(`/api/ActionItems/${draggedTask.id}/status`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: newStatus, userId })
                });

                const data = await response.json();
                if (data.success) {
                    toast.success(`Task moved to ${newStatus}`);
                } else {
                    toast.error(data.message || 'Failed to update status');
                    setDraggedTask(null);
                    return;
                }
            } catch (error) {
                Sentry.captureException(error);
                console.error('Error updating status:', error);
                toast.error('Failed to update status');
                setDraggedTask(null);
                return;
            }
        }

        // Refresh data after changes
        fetchProject();
        fetchStats();
        setDraggedTask(null);
    };

    /**
     * Handle drop on a milestone swimlane (assign task to milestone)
     * This updates the task's milestoneId directly
     */
    const handleDropOnMilestone = async (e, milestoneId, newStatus) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!draggedTask) {
            setDraggedTask(null);
            return;
        }

        // Find the target milestone
        const targetMilestone = milestones.find(m => m.milestoneId === milestoneId);
        
        try {
            // Update task's milestoneId and status
            const updateData = {
                userId,
                status: newStatus || draggedTask.status,
                milestoneId: milestoneId || null
            };

            const response = await apiFetch(`/api/ActionItems/${draggedTask.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();
            if (data.success) {
                // Also update all subtasks to the same milestone
                const subtasks = getSubtasks(draggedTask.id);
                for (const subtask of subtasks) {
                    await apiFetch(`/api/ActionItems/${subtask.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            userId,
                            milestoneId: milestoneId || null
                        })
                    });
                }
                
                toast.success(targetMilestone 
                    ? `Task moved to milestone: ${targetMilestone.name}` 
                    : 'Task unassigned from milestone');
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to move task to milestone');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error moving task to milestone:', error);
            toast.error('Failed to move task to milestone');
        } finally {
            setDraggedTask(null);
        }
    };

    /**
     * Handle drop on another task card (reparent subtask)
     */
    const handleDropOnTask = async (e, targetTask) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!draggedTask) {
            setDraggedTask(null);
            return;
        }

        // Prevent dropping on self
        if (draggedTask.id === targetTask.id) {
            setDraggedTask(null);
            return;
        }

        // Prevent creating circular parent relationships
        const isTargetChildOfDragged = (taskId, parentId) => {
            if (!parentId) return false;
            if (parentId === taskId) return true;
            const parent = tasks.find(t => t.id === parentId);
            return parent ? isTargetChildOfDragged(taskId, parent.parentActionItemId) : false;
        };

        if (isTargetChildOfDragged(draggedTask.id, targetTask.id)) {
            toast.error('Cannot create circular parent relationship');
            setDraggedTask(null);
            return;
        }

        try {
            const response = await apiFetch(`/api/ActionItems/${draggedTask.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    parentActionItemId: targetTask.id
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success(`Task "${draggedTask.title || 'Subtask'}" is now a subtask of "${targetTask.title || 'Task'}"`);
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to reparent task');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error reparenting task:', error);
            toast.error('Failed to reparent task');
        } finally {
            setDraggedTask(null);
        }
    };

    /**
     * Handle drop to remove parent (make independent task)
     */
    const handleDropToUnparent = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!draggedTask || !draggedTask.parentActionItemId) {
            setDraggedTask(null);
            return;
        }

        try {
            const response = await apiFetch(`/api/ActionItems/${draggedTask.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    parentActionItemId: null
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success(`Task "${draggedTask.title || 'Task'}" is now an independent task`);
                fetchProject();
                fetchStats();
            } else {
                toast.error(data.message || 'Failed to unparent task');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error unparenting task:', error);
            toast.error('Failed to unparent task');
        } finally {
            setDraggedTask(null);
        }
    };

    /**
     * Get subtasks for a given parent task
     */
    const getSubtasks = (parentId) => {
        return tasks.filter(t => t.parentActionItemId === parentId);
    };

    /**
     * Get tasks organized by milestone.
     * Returns an object with milestoneId as key (or 'unassigned' for tasks without milestone)
     * and array of tasks as value.
     * Uses the task's milestoneId field directly for proper milestone association.
     */
    const getTasksByMilestone = () => {
        const tasksByMilestone = { unassigned: [] };
        
        // Initialize all milestones
        milestones.forEach(m => {
            tasksByMilestone[m.milestoneId] = [];
        });
        
        // Get only root-level tasks (no parent)
        const rootTasks = tasks.filter(t => !t.parentActionItemId);
        
        // Assign tasks to milestones based on task's milestoneId
        rootTasks.forEach(task => {
            if (task.milestoneId && tasksByMilestone[task.milestoneId]) {
                // Task has a milestoneId that exists in our milestones
                tasksByMilestone[task.milestoneId].push(task);
            } else {
                // Task has no milestoneId or milestone doesn't exist
                tasksByMilestone.unassigned.push(task);
            }
        });
        
        return tasksByMilestone;
    };

    // ==================== KANBAN COLUMN MANAGEMENT ====================

    /**
     * Initialize custom columns from defaults
     */
    const handleInitializeColumns = async () => {
        try {
            const response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns/initialize`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Columns initialized for customization');
                fetchKanbanColumns();
            } else {
                toast.error(data.message || 'Failed to initialize columns');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error initializing columns:', error);
            toast.error('Failed to initialize columns');
        }
    };

    /**
     * Open modal to create a new column
     */
    const handleCreateColumn = () => {
        setEditingColumn(null);
        setColumnForm({
            name: '',
            statusValue: '',
            color: '#3498db',
            isCompletedColumn: false,
            wipLimit: 0
        });
        setShowColumnModal(true);
    };

    /**
     * Open modal to edit an existing column
     */
    const handleEditColumn = (column) => {
        setEditingColumn(column);
        setColumnForm({
            name: column.name,
            statusValue: column.statusValue,
            color: column.color || '#3498db',
            isCompletedColumn: column.isCompletedColumn || false,
            wipLimit: column.wipLimit || 0
        });
        setShowColumnModal(true);
    };

    /**
     * Save column (create or update)
     */
    const handleSaveColumn = async () => {
        if (!columnForm.name.trim()) {
            toast.error('Column name is required');
            return;
        }

        try {
            const payload = {
                name: columnForm.name,
                statusValue: columnForm.statusValue || columnForm.name,
                color: columnForm.color,
                isCompletedColumn: columnForm.isCompletedColumn,
                wipLimit: parseInt(columnForm.wipLimit) || 0,
                userId
            };

            let response;
            if (editingColumn?.columnId) {
                response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns/${editingColumn.columnId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();
            if (data.success) {
                toast.success(editingColumn ? 'Column updated' : 'Column created');
                setShowColumnModal(false);
                fetchKanbanColumns();
            } else {
                toast.error(data.message || 'Failed to save column');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving column:', error);
            toast.error('Failed to save column');
        }
    };

    /**
     * Delete a column
     */
    const handleDeleteColumn = async (columnId) => {
        if (!window.confirm('Delete this column? Tasks in this column will need to be moved first.')) return;

        try {
            const response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns/${columnId}?userId=${userId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Column deleted');
                fetchKanbanColumns();
            } else {
                toast.error(data.message || 'Failed to delete column');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error deleting column:', error);
            toast.error('Failed to delete column');
        }
    };

    /**
     * Reset columns to defaults
     */
    const handleResetColumns = async () => {
        if (!window.confirm('Reset to default columns? All custom columns will be removed.')) return;

        try {
            const response = await apiFetch(`/api/Projects/${projectId}/KanbanColumns/reset?userId=${userId}`, {
                method: 'DELETE'
            });

            const data = await response.json();
            if (data.success) {
                toast.success('Columns reset to defaults');
                fetchKanbanColumns();
            } else {
                toast.error(data.message || 'Failed to reset columns');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error resetting columns:', error);
            toast.error('Failed to reset columns');
        }
    };

    // ==================== END KANBAN COLUMN MANAGEMENT ====================

    /**
     * Build hierarchical task tree
     */
    const buildTaskTree = () => {
        const taskMap = new Map();
        const rootTasks = [];

        // First pass: create map of all tasks
        tasks.forEach(task => {
            taskMap.set(task.id, { ...task, children: [] });
        });

        // Second pass: build tree structure
        tasks.forEach(task => {
            const taskNode = taskMap.get(task.id);
            if (task.parentActionItemId && taskMap.has(task.parentActionItemId)) {
                taskMap.get(task.parentActionItemId).children.push(taskNode);
            } else {
                rootTasks.push(taskNode);
            }
        });

        return rootTasks;
    };

    /**
     * Render a task row with its subtasks
     */
    const renderTaskRow = (task, level = 0) => {
        const hasChildren = task.children && task.children.length > 0;
        const isExpanded = expandedTasks.has(task.id);
        const isCompleted = task.status === 'Closed' || task.status === 'Completed';
        const completedChildren = hasChildren ? task.children.filter(c => c.status === 'Closed' || c.status === 'Completed').length : 0;
        const percentComplete = hasChildren 
            ? Math.round((completedChildren / task.children.length) * 100) 
            : (isCompleted ? 100 : 0);

        return (
            <React.Fragment key={task.id}>
                <div 
                    className={`task-row level-${level} ${isCompleted ? 'completed' : ''} ${hasChildren ? 'has-subtasks' : ''} clickable`}
                    onClick={() => handleOpenTaskDetail(task)}
                >
                    <div className="task-expand" style={{ marginLeft: `${level * 24}px` }} onClick={(e) => e.stopPropagation()}>
                        {hasChildren ? (
                            <button 
                                className="expand-btn"
                                onClick={(e) => { e.stopPropagation(); toggleTaskExpanded(task.id); }}
                                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            </button>
                        ) : (
                            <span className="no-expand"></span>
                        )}
                    </div>

                    <div className="task-info">
                        <div className="task-title">
                            <span className={isCompleted ? 'strikethrough' : ''}>
                                {task.title || task.description || 'Untitled Task'}
                            </span>
                            {level > 0 && <span className="subtask-badge">Subtask</span>}
                            {hasChildren && (
                                <span className="subtask-count" title={`${completedChildren}/${task.children.length} subtasks completed`}>
                                    <FaSitemap /> {completedChildren}/{task.children.length}
                                </span>
                            )}
                        </div>
                        {task.description && task.title && (
                            <div className="task-description">{task.description}</div>
                        )}
                    </div>

                    <div className="task-status" onClick={(e) => e.stopPropagation()}>
                        <select 
                            value={task.status || 'Open'}
                            onChange={(e) => handleQuickStatusUpdate(task.id, e.target.value)}
                            className={`status-select status-${task.status?.toLowerCase().replace(' ', '-')}`}
                        >
                            {TASK_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>

                    <div className="task-priority">
                        <span className={`priority-badge priority-${task.priority?.toLowerCase()}`}>
                            {task.priority || 'Medium'}
                        </span>
                    </div>

                    <div className="task-assignee">
                        {getUserName(task.assignedToUserID)}
                    </div>

                    <div className="task-dates">
                        {task.expectedCompletionDate && (
                            <span className={new Date(task.expectedCompletionDate) < new Date() && !isCompleted ? 'overdue' : ''}>
                                {formatDate(task.expectedCompletionDate)}
                            </span>
                        )}
                    </div>

                    <div className="task-progress-cell">
                        <div className="task-progress-mini">
                            <div className="progress-bar-mini">
                                <div 
                                    className="progress-fill-mini"
                                    style={{ width: `${percentComplete}%` }}
                                />
                            </div>
                            <span className="progress-label">{percentComplete}%</span>
                        </div>
                    </div>

                    <div className="task-actions" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="btn-icon btn-view" 
                            onClick={(e) => { e.stopPropagation(); handleOpenTaskDetail(task); }}
                            title="View details"
                            aria-label="View details"
                        >
                            <FaEye />
                        </button>
                        <button 
                            className="btn-icon btn-add" 
                            onClick={(e) => { e.stopPropagation(); handleCreateTask(task.id); }}
                            title="Add subtask"
                            aria-label="Add subtask"
                        >
                            <FaPlus />
                        </button>
                        <button 
                            className="btn-icon btn-edit" 
                            onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                            title="Edit task"
                            aria-label="Edit task"
                        >
                            <FaEdit />
                        </button>
                        <button 
                            className="btn-icon btn-danger" 
                            onClick={(e) => { e.stopPropagation(); handleRemoveTask(task.id); }}
                            title="Remove from project"
                            aria-label="Remove from project"
                        >
                            <FaTrashAlt />
                        </button>
                    </div>
                </div>

                {/* Render children if expanded */}
                {hasChildren && isExpanded && (
                    task.children.map(child => renderTaskRow(child, level + 1))
                )}
            </React.Fragment>
        );
    };

    if (loading) {
        return (
            <div className="project-detail-page">
                <div className="loading-container">
                    <FaSpinner className="fa-spin fa-3x" />
                    <p>Loading project...</p>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="project-detail-page">
                <div className="error-container">
                    <FaExclamationTriangle className="fa-3x" />
                    <p>Project not found</p>
                    <button className="btn btn-primary" onClick={() => navigate('/projects')}>
                        Back to Projects
                    </button>
                </div>
            </div>
        );
    }

    const taskTree = buildTaskTree();

    return (
        <div className="project-detail-page">
            <PageHeader
                className="project-detail-header"
                title={project.name}
                description={project.description}
                icon={
                    <div
                        className="project-color-indicator"
                        style={{ backgroundColor: project.color || '#3498db' }}
                        aria-hidden="true"
                    />
                }
                meta={
                    <>
                        <span className={`status-badge status-${project.status?.toLowerCase().replace(' ', '-')}`}>
                            {project.status}
                        </span>
                        <span className={`priority-badge priority-${project.priority?.toLowerCase()}`}>
                            {project.priority}
                        </span>
                        {project.department && (
                            <span className="department-badge">{project.department}</span>
                        )}
                    </>
                }
                secondaryActions={
                    <button
                        className="back-btn"
                        onClick={() => navigate('/projects')}
                        title="Back to projects"
                        aria-label="Back to projects"
                    >
                        <FaArrowLeft />
                    </button>
                }
                primaryAction={
                    <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                        <FaPlus /> Add Task
                    </button>
                }
            />

            {/* Project Info */}
            <div className="project-info-section">
                <div className="info-grid">
                    <div className="info-card">
                        <div className="info-icon">
                            <FaUser />
                        </div>
                        <div className="info-content">
                            <span className="label">Assigned To</span>
                            <span className="value">{getUserName(project.assignedToUserId)}</span>
                        </div>
                    </div>

                    <div className="info-card">
                        <div className="info-icon">
                            <FaCalendarAlt />
                        </div>
                        <div className="info-content">
                            <span className="label">Start Date</span>
                            <span className="value">{formatDate(project.startDate) || 'Not set'}</span>
                        </div>
                    </div>

                    <div className="info-card">
                        <div className="info-icon">
                            <FaFlagCheckered />
                        </div>
                        <div className="info-content">
                            <span className="label">Due Date</span>
                            <span className="value">{formatDate(project.expectedCompletionDate) || 'Not set'}</span>
                        </div>
                    </div>

                    <div className="info-card">
                        <div className="info-icon">
                            <FaTasks />
                        </div>
                        <div className="info-content">
                            <span className="label">Progress</span>
                            <div className="progress-bar-container">
                                <div className="progress-bar">
                                    <div 
                                        className="progress-fill" 
                                        style={{ width: `${project.progressPercent || 0}%` }}
                                    ></div>
                                </div>
                                <span className="progress-text">{project.progressPercent || 0}%</span>
                            </div>
                        </div>
                    </div>
                </div>

                {project.description && (
                    <div className="project-description">
                        <h3>Description</h3>
                        <p>{project.description}</p>
                    </div>
                )}
            </div>

            {/* Stats Section */}
            {stats && (
                <div className="stats-section">
                    <div className="stat-card">
                        <span className="stat-value">{stats.totalTasks}</span>
                        <span className="stat-label">Total Tasks</span>
                    </div>
                    <div className="stat-card completed">
                        <span className="stat-value">{stats.completedTasks}</span>
                        <span className="stat-label">Completed</span>
                    </div>
                    <div className="stat-card in-progress">
                        <span className="stat-value">{stats.inProgressTasks}</span>
                        <span className="stat-label">In Progress</span>
                    </div>
                    <div className="stat-card open">
                        <span className="stat-value">{stats.openTasks}</span>
                        <span className="stat-label">Open</span>
                    </div>
                    <div className={`stat-card overdue ${stats.overdueTasks > 0 ? 'has-overdue' : ''}`}>
                        <span className="stat-value">{stats.overdueTasks}</span>
                        <span className="stat-label">Overdue</span>
                    </div>
                </div>
            )}

            {/* View Toggle Tabs */}
            <div className="view-toggle-tabs">
                <button 
                    className={`tab-btn ${activeTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tasks')}
                >
                    <FaListAlt /> List
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'kanban' ? 'active' : ''}`}
                    onClick={() => setActiveTab('kanban')}
                >
                    <FaColumns /> Kanban
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'gantt' ? 'active' : ''}`}
                    onClick={() => setActiveTab('gantt')}
                >
                    <FaChartLine /> Gantt
                </button>
                <button 
                    className={`tab-btn ${activeTab === 'milestones' ? 'active' : ''}`}
                    onClick={() => setActiveTab('milestones')}
                >
                    <FaFlag /> Milestones
                    {milestones.length > 0 && <span className="tab-badge">{milestones.length}</span>}
                </button>
            </div>

            {/* Gantt Chart View */}
            {activeTab === 'gantt' && (
            <div className="gantt-section">
                <div className="section-header">
                    <h2><FaChartLine /> Gantt Chart</h2>
                    <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                        <FaPlus /> Add Task
                    </button>
                </div>

                {tasks.length === 0 ? (
                    <div className="empty-tasks">
                        <FaChartLine className="fa-3x" />
                        <h3>No Tasks Yet</h3>
                        <p>Create your first task to see the Gantt chart.</p>
                        <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                            <FaPlus /> Create First Task
                        </button>
                    </div>
                ) : (
                    <div className="gantt-container">
                        <div className="gantt-chart-wrapper">
                            <GanttChart
                                project={project}
                                tasks={tasks}
                                milestones={milestones}
                                getSubtasks={getSubtasks}
                                getTasksByMilestone={getTasksByMilestone}
                                handleOpenTaskDetail={handleOpenTaskDetail}
                            />
                        </div>
                    </div>
                )}
            </div>
            )}

            {/* Tasks Section - List View with Milestone Grouping */}
            {activeTab === 'tasks' && (
            <div className="tasks-section">
                <div className="section-header">
                    <h2><FaListAlt /> Tasks</h2>
                    <span className="task-count">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
                </div>

                {tasks.length === 0 ? (
                    <div className="empty-tasks">
                        <FaClipboardList className="fa-3x" />
                        <h3>No Tasks Yet</h3>
                        <p>Create your first task to start managing this project.</p>
                        <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                            <FaPlus /> Create First Task
                        </button>
                    </div>
                ) : (
                    <div className="tasks-grouped-by-milestone">
                        {(() => {
                            const tasksByMilestone = getTasksByMilestone();
                            const sortedMilestones = [...milestones].sort((a, b) => {
                                if (!a.targetDate) return 1;
                                if (!b.targetDate) return -1;
                                return new Date(a.targetDate) - new Date(b.targetDate);
                            });
                            
                            // Helper to build task tree for a set of tasks
                            const buildMilestoneTaskTree = (milestoneTasks) => {
                                const taskMap = new Map();
                                const rootTasks = [];
                                
                                // Get all related subtasks for these milestone tasks
                                const allTaskIds = new Set(milestoneTasks.map(t => t.id));
                                const subtasksForMilestone = tasks.filter(t => 
                                    t.parentActionItemId && allTaskIds.has(t.parentActionItemId)
                                );
                                const allTasks = [...milestoneTasks, ...subtasksForMilestone];
                                
                                allTasks.forEach(task => {
                                    taskMap.set(task.id, { ...task, children: [] });
                                });
                                
                                allTasks.forEach(task => {
                                    const taskNode = taskMap.get(task.id);
                                    if (task.parentActionItemId && taskMap.has(task.parentActionItemId)) {
                                        taskMap.get(task.parentActionItemId).children.push(taskNode);
                                    } else if (!task.parentActionItemId) {
                                        rootTasks.push(taskNode);
                                    }
                                });
                                
                                return rootTasks;
                            };
                            
                            return (
                                <>
                                    {/* Milestone Groups */}
                                    {sortedMilestones.map(milestone => {
                                        const milestoneTasks = tasksByMilestone[milestone.milestoneId] || [];
                                        const milestoneTaskTree = buildMilestoneTaskTree(milestoneTasks);
                                        
                                        if (milestoneTasks.length === 0) return null;
                                        
                                        return (
                                            <div key={milestone.milestoneId} className="milestone-task-group">
                                                <div className="milestone-group-header" style={{ borderLeftColor: milestone.color || '#f39c12' }}>
                                                    <div className="milestone-info">
                                                        {(() => { const Icon = getMilestoneIcon(milestone.icon); return <Icon style={{ color: milestone.color || '#f39c12' }} />; })()}
                                                        <span className="milestone-name">{milestone.name}</span>
                                                        {milestone.isCritical && <span className="critical-badge"><FaExclamation /></span>}
                                                        <span className={`milestone-status-badge status-${milestone.status?.toLowerCase().replace(' ', '-')}`}>
                                                            {milestone.status}
                                                        </span>
                                                        {milestone.targetDate && (
                                                            <span className="milestone-target-date">
                                                                <FaCalendarAlt /> {formatDate(milestone.targetDate)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="task-count-badge">{milestoneTasks.length} task{milestoneTasks.length !== 1 ? 's' : ''}</span>
                                                </div>
                                                <div className="tasks-table">
                                                    <div className="tasks-header">
                                                        <div className="header-expand"></div>
                                                        <div className="header-info">Task</div>
                                                        <div className="header-status">Status</div>
                                                        <div className="header-priority">Priority</div>
                                                        <div className="header-assignee">Assignee</div>
                                                        <div className="header-dates">Due Date</div>
                                                        <div className="header-progress">% Complete</div>
                                                        <div className="header-actions">Actions</div>
                                                    </div>
                                                    <div className="tasks-body">
                                                        {milestoneTaskTree.map(task => renderTaskRow(task))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Unassigned Tasks */}
                                    {tasksByMilestone.unassigned && tasksByMilestone.unassigned.length > 0 && (
                                        <div className="milestone-task-group unassigned-group">
                                            <div className="milestone-group-header" style={{ borderLeftColor: '#95a5a6' }}>
                                                <div className="milestone-info">
                                                    <FaInbox style={{ color: '#95a5a6' }} />
                                                    <span className="milestone-name">Unassigned to Milestone</span>
                                                </div>
                                                <span className="task-count-badge">{tasksByMilestone.unassigned.length} task{tasksByMilestone.unassigned.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="tasks-table">
                                                <div className="tasks-header">
                                                    <div className="header-expand"></div>
                                                    <div className="header-info">Task</div>
                                                    <div className="header-status">Status</div>
                                                    <div className="header-priority">Priority</div>
                                                    <div className="header-assignee">Assignee</div>
                                                    <div className="header-dates">Due Date</div>
                                                    <div className="header-progress">% Complete</div>
                                                    <div className="header-actions">Actions</div>
                                                </div>
                                                <div className="tasks-body">
                                                    {buildMilestoneTaskTree(tasksByMilestone.unassigned).map(task => renderTaskRow(task))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* If no milestones exist, show all tasks in a simple list */}
                                    {milestones.length === 0 && (
                                        <div className="tasks-table">
                                            <div className="tasks-header">
                                                <div className="header-expand"></div>
                                                <div className="header-info">Task</div>
                                                <div className="header-status">Status</div>
                                                <div className="header-priority">Priority</div>
                                                <div className="header-assignee">Assignee</div>
                                                <div className="header-dates">Due Date</div>
                                                <div className="header-progress">% Complete</div>
                                                <div className="header-actions">Actions</div>
                                            </div>
                                            <div className="tasks-body">
                                                {taskTree.map(task => renderTaskRow(task))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>
            )}

            {/* Milestones View */}
            {activeTab === 'milestones' && (
            <div className="milestones-section">
                <div className="section-header">
                    <h2><FaFlag /> Milestones</h2>
                    <button className="btn btn-primary" onClick={handleCreateMilestone}>
                        <FaPlus /> Add Milestone
                    </button>
                </div>

                {milestones.length === 0 ? (
                    <div className="empty-tasks">
                        <FaFlagCheckered className="fa-3x" />
                        <h3>No Milestones Yet</h3>
                        <p>Create milestones to track key deliverables and checkpoints in your project.</p>
                        <button className="btn btn-primary" onClick={handleCreateMilestone}>
                            <FaPlus /> Create First Milestone
                        </button>
                    </div>
                ) : (
                    <div className="milestones-list">
                        {(() => {
                            // Compute tasksByMs once here to avoid calling getTasksByMilestone()
                            // inside the .map() which would cause O(m*(m+t)) complexity.
                            const tasksByMs = getTasksByMilestone();

                            return [...milestones].sort((a, b) => {
                                if (!a.targetDate) return 1;
                                if (!b.targetDate) return -1;
                                return new Date(a.targetDate) - new Date(b.targetDate);
                            }).map(milestone => {
                                const milestoneTasks = tasksByMs[milestone.milestoneId] || [];
                                const completedTasks = milestoneTasks.filter(t => t.status === 'Closed' || t.status === 'Completed').length;
                                const MilestoneIcon = MILESTONE_ICONS.find(i => i.value === milestone.icon)?.Icon || FaFlag;
                                const isOverdue = milestone.targetDate && new Date(milestone.targetDate) < new Date() && milestone.status !== 'Achieved';

                            return (
                                <div key={milestone.milestoneId} className={`milestone-card ${milestone.status === 'Achieved' ? 'achieved' : ''} ${isOverdue ? 'overdue' : ''}`}>
                                    <div className="milestone-card-indicator" style={{ backgroundColor: milestone.color || '#2ecc71' }} />
                                    <div className="milestone-card-content">
                                        <div className="milestone-card-header">
                                            <div className="milestone-card-title">
                                                <MilestoneIcon style={{ color: milestone.color || '#2ecc71' }} />
                                                <h3>{milestone.name}</h3>
                                                {milestone.isCritical && <span className="critical-badge"><FaExclamation /> Critical</span>}
                                            </div>
                                            <div className="milestone-card-actions">
                                                <button 
                                                    className="btn-icon btn-edit"
                                                    onClick={() => handleEditMilestone(milestone)}
                                                    title="Edit milestone"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button 
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDeleteMilestone(milestone.milestoneId)}
                                                    title="Delete milestone"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </div>
                                        </div>

                                        {milestone.description && (
                                            <p className="milestone-card-description">{milestone.description}</p>
                                        )}

                                        <div className="milestone-card-meta">
                                            <span className={`milestone-status-badge status-${milestone.status?.toLowerCase().replace(' ', '-')}`}>
                                                {milestone.status || 'Pending'}
                                            </span>
                                            {milestone.targetDate && (
                                                <span className={`milestone-date ${isOverdue ? 'overdue' : ''}`}>
                                                    <FaCalendarAlt /> {formatDate(milestone.targetDate)}
                                                </span>
                                            )}
                                            {milestone.achievedDate && (
                                                <span className="milestone-achieved-date">
                                                    <FaCheckCircle /> Achieved: {formatDate(milestone.achievedDate)}
                                                </span>
                                            )}
                                            <span className="milestone-task-count">
                                                <FaTasks /> {completedTasks}/{milestoneTasks.length} tasks
                                            </span>
                                            {milestone.notifyOnAchievement && (
                                                <span className="milestone-notify-badge" title="Notifications enabled">
                                                    <FaBell />
                                                </span>
                                            )}
                                        </div>

                                        {milestoneTasks.length > 0 && (
                                            <div className="milestone-progress">
                                                <div className="progress-bar">
                                                    <div 
                                                        className="progress-fill"
                                                        style={{ 
                                                            width: `${milestoneTasks.length > 0 ? (completedTasks / milestoneTasks.length) * 100 : 0}%`,
                                                            backgroundColor: milestone.color || '#2ecc71'
                                                        }}
                                                    />
                                                </div>
                                                <span className="progress-text">
                                                    {milestoneTasks.length > 0 ? Math.round((completedTasks / milestoneTasks.length) * 100) : 0}%
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        });
                        })()}
                    </div>
                )}
            </div>
            )}

            {/* Kanban View */}
            {activeTab === 'kanban' && (
            <div className="kanban-section">
                <div className="section-header">
                    <h2><FaColumns /> Kanban Board</h2>
                    <div className="header-actions">
                        {usingDefaultColumns ? (
                            <button 
                                className="btn btn-outline" 
                                onClick={handleInitializeColumns}
                                title="Customize columns for this project"
                            >
                                <FaCog /> Customize Columns
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="btn btn-outline" 
                                    onClick={handleCreateColumn}
                                    title="Add a new column"
                                >
                                    <FaPlus /> Add Column
                                </button>
                                <button 
                                    className="btn btn-outline-secondary" 
                                    onClick={handleResetColumns}
                                    title="Reset to default columns"
                                >
                                    <FaUndo /> Reset
                                </button>
                            </>
                        )}
                        <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                            <FaPlus /> Add Task
                        </button>
                    </div>
                </div>

                {tasks.length === 0 ? (
                    <div className="empty-tasks">
                        <FaClipboardList className="fa-3x" />
                        <h3>No Tasks Yet</h3>
                        <p>Create your first task to start managing this project.</p>
                        <button className="btn btn-primary" onClick={() => handleCreateTask()}>
                            <FaPlus /> Create First Task
                        </button>
                    </div>
                ) : (
                    <div className="kanban-with-swimlanes">
                        {/* Column Headers */}
                        <div className="kanban-column-headers" style={{ gridTemplateColumns: `200px repeat(${kanbanColumns.length}, 1fr)` }}>
                            <div className="swimlane-label-header">Milestone</div>
                            {kanbanColumns.map(column => (
                                <div key={column.id} className="column-header-cell" style={{ borderTopColor: column.color }}>
                                    <span className="column-title">{column.name}</span>
                                    {!usingDefaultColumns && column.columnId && (
                                        <div className="column-actions">
                                            <button 
                                                className="btn-icon-sm"
                                                onClick={() => handleEditColumn(column)}
                                                title="Edit column"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button 
                                                className="btn-icon-sm btn-danger-sm"
                                                onClick={() => handleDeleteColumn(column.columnId)}
                                                title="Delete column"
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        {/* Milestone Swimlanes */}
                        {(() => {
                            const tasksByMilestone = getTasksByMilestone();
                            const sortedMilestones = [...milestones].sort((a, b) => {
                                if (!a.targetDate) return 1;
                                if (!b.targetDate) return -1;
                                return new Date(a.targetDate) - new Date(b.targetDate);
                            });
                            
                            return (
                                <>
                                    {/* Milestone Swimlanes */}
                                    {sortedMilestones.map(milestone => {
                                        const milestoneTasks = tasksByMilestone[milestone.milestoneId] || [];
                                        return (
                                            <KanbanSwimlane
                                                key={milestone.milestoneId}
                                                milestone={milestone}
                                                tasks={milestoneTasks}
                                                columns={kanbanColumns}
                                                draggedTask={draggedTask}
                                                getSubtasks={getSubtasks}
                                                getUserName={getUserName}
                                                getMilestoneIcon={getMilestoneIcon}
                                                onDragStart={handleDragStart}
                                                onDragOver={handleDragOver}
                                                onDropOnMilestone={handleDropOnMilestone}
                                                onDropOnTask={handleDropOnTask}
                                                onOpenDetail={handleOpenTaskDetail}
                                                onCreateSubtask={handleCreateTask}
                                                onEditTask={handleEditTask}
                                            />
                                        );
                                    })}

                                    {/* Unassigned Tasks Swimlane */}
                                    {tasksByMilestone.unassigned && tasksByMilestone.unassigned.length > 0 && (
                                        <KanbanSwimlane
                                            isUnassigned
                                            tasks={tasksByMilestone.unassigned}
                                            columns={kanbanColumns}
                                            draggedTask={draggedTask}
                                            getSubtasks={getSubtasks}
                                            getUserName={getUserName}
                                            onDragStart={handleDragStart}
                                            onDragOver={handleDragOver}
                                            onDropStatus={handleDrop}
                                            onDropOnTask={handleDropOnTask}
                                            onDropToUnparent={handleDropToUnparent}
                                            onOpenDetail={handleOpenTaskDetail}
                                            onCreateSubtask={handleCreateTask}
                                            onEditTask={handleEditTask}
                                        />
                                    )}

                                    {/* Show helpful message if no milestones */}
                                    {milestones.length === 0 && (
                                        <div className="no-milestones-message">
                                            <FaInfoCircle />
                                            <p>No milestones defined. All tasks are shown in a single board.</p>
                                            <button 
                                                className="btn btn-outline btn-sm"
                                                onClick={handleCreateMilestone}
                                            >
                                                <FaPlus /> Create Milestone
                                            </button>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>
            )}

            {/* Task Modal */}
            {showTaskModal && (
                <div className="modal-backdrop" onClick={() => setShowTaskModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {editingTask ? 'Edit Task' : parentTaskId ? 'Create Subtask' : 'Create Task'}
                            </h3>
                            <button className="close-btn" onClick={() => setShowTaskModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSaveTask} className="modal-body">
                            <div className="form-group">
                                <label htmlFor="title">Task Title *</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={taskForm.title}
                                    onChange={handleTaskInputChange}
                                    required
                                    placeholder="Enter task title"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={taskForm.description}
                                    onChange={handleTaskInputChange}
                                    rows={3}
                                    placeholder="Enter task description"
                                />
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="status">Status</label>
                                    <select
                                        id="status"
                                        name="status"
                                        value={taskForm.status}
                                        onChange={handleTaskInputChange}
                                    >
                                        {TASK_STATUSES.map(s => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="priority">Priority</label>
                                    <select
                                        id="priority"
                                        name="priority"
                                        value={taskForm.priority}
                                        onChange={handleTaskInputChange}
                                    >
                                        {PRIORITY_OPTIONS.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="assignedToUserId">Assigned To</label>
                                    <select
                                        id="assignedToUserId"
                                        name="assignedToUserId"
                                        value={taskForm.assignedToUserId}
                                        onChange={handleTaskInputChange}
                                    >
                                        <option value="">Unassigned</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="department">Department</label>
                                    <select
                                        id="department"
                                        name="department"
                                        value={taskForm.department}
                                        onChange={handleTaskInputChange}
                                    >
                                        <option value="">Select Department</option>
                                        {departments.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Milestone Selection (only for root tasks, not subtasks) */}
                            {!parentTaskId && milestones.length > 0 && (
                                <div className="form-group">
                                    <label htmlFor="milestoneId">
                                        <FaFlag style={{ marginRight: '6px', color: '#f39c12' }} />
                                        Milestone
                                    </label>
                                    <select
                                        id="milestoneId"
                                        name="milestoneId"
                                        value={taskForm.milestoneId}
                                        onChange={handleTaskInputChange}
                                    >
                                        <option value="">No Milestone (Unassigned)</option>
                                        {milestones.map(m => (
                                            <option key={m.milestoneId} value={m.milestoneId}>
                                                {m.name} {m.targetDate ? `(Due: ${formatDate(m.targetDate)})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <small className="form-hint">Assign this task to a project milestone</small>
                                </div>
                            )}

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="startDate">Start Date</label>
                                    <DateInput
                                        id="startDate"
                                        name="startDate"
                                        value={taskForm.startDate}
                                        onChange={handleTaskInputChange}
                                        placeholder="Select start date"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="expectedCompletionDate">Due Date</label>
                                    <DateInput
                                        id="expectedCompletionDate"
                                        name="expectedCompletionDate"
                                        value={taskForm.expectedCompletionDate}
                                        onChange={handleTaskInputChange}
                                        placeholder="Select due date"
                                    />
                                </div>
                            </div>

                            {/* Dynamic Template Fields */}
                            {!loadingTemplates && getActiveTemplate() && (
                                <div className="template-fields-section">
                                    <div className="template-header">
                                        <FaCog />
                                        <span>{getActiveTemplate().displayName || 'Additional Fields'}</span>
                                    </div>
                                    {getTemplateFields(getActiveTemplate()).map(field => (
                                        renderTemplateField(
                                            field,
                                            dynamicFormFields[field.name],
                                            handleDynamicFieldChange
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowTaskModal(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingTask ? 'Update Task' : 'Create Task'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Milestone Modal */}
            {showMilestoneModal && (
                <div className="modal-backdrop" onClick={() => setShowMilestoneModal(false)}>
                    <div className="modal-content milestone-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
                            </h3>
                            <button className="close-btn" onClick={() => setShowMilestoneModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="milestoneName">Milestone Name *</label>
                                <input
                                    type="text"
                                    id="milestoneName"
                                    value={milestoneForm.name}
                                    onChange={(e) => setMilestoneForm({...milestoneForm, name: e.target.value})}
                                    required
                                    placeholder="Enter milestone name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="milestoneDescription">Description</label>
                                <textarea
                                    id="milestoneDescription"
                                    value={milestoneForm.description}
                                    onChange={(e) => setMilestoneForm({...milestoneForm, description: e.target.value})}
                                    rows={3}
                                    placeholder="What does this milestone represent?"
                                />
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="milestoneTargetDate">Target Date</label>
                                    <input
                                        type="date"
                                        id="milestoneTargetDate"
                                        value={milestoneForm.targetDate}
                                        onChange={(e) => setMilestoneForm({...milestoneForm, targetDate: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="progressWeight">Progress Weight (%)</label>
                                    <input
                                        type="number"
                                        id="progressWeight"
                                        value={milestoneForm.progressWeight}
                                        onChange={(e) => setMilestoneForm({...milestoneForm, progressWeight: e.target.value})}
                                        min="0"
                                        max="100"
                                        placeholder="e.g., 25"
                                    />
                                </div>
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="milestoneColor">Color</label>
                                    <div className="color-picker">
                                        <input
                                            type="color"
                                            id="milestoneColor"
                                            value={milestoneForm.color}
                                            onChange={(e) => setMilestoneForm({...milestoneForm, color: e.target.value})}
                                        />
                                        <div className="color-presets">
                                            {MILESTONE_COLORS.map(color => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    className={`color-preset ${milestoneForm.color === color ? 'selected' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => setMilestoneForm({...milestoneForm, color})}
                                                    aria-label={`Select color ${color}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="milestoneIcon">Icon</label>
                                    <select
                                        id="milestoneIcon"
                                        value={milestoneForm.icon}
                                        onChange={(e) => setMilestoneForm({...milestoneForm, icon: e.target.value})}
                                    >
                                        {MILESTONE_ICONS.map(icon => (
                                            <option key={icon.value} value={icon.value}>{icon.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4><FaBell /> Notification Settings</h4>
                                <div className="form-check-group">
                                    <label className="form-check">
                                        <input
                                            type="checkbox"
                                            checked={milestoneForm.notifyOnAchievement}
                                            onChange={(e) => setMilestoneForm({...milestoneForm, notifyOnAchievement: e.target.checked})}
                                        />
                                        <span>Send email notification when achieved</span>
                                    </label>
                                    <label className="form-check">
                                        <input
                                            type="checkbox"
                                            checked={milestoneForm.notifyStakeholders}
                                            onChange={(e) => setMilestoneForm({...milestoneForm, notifyStakeholders: e.target.checked})}
                                        />
                                        <span>Notify all project stakeholders</span>
                                    </label>
                                    <label className="form-check">
                                        <input
                                            type="checkbox"
                                            checked={milestoneForm.isCritical}
                                            onChange={(e) => setMilestoneForm({...milestoneForm, isCritical: e.target.checked})}
                                        />
                                        <span>Mark as critical milestone</span>
                                    </label>
                                </div>
                            </div>

                            {milestoneForm.notifyOnAchievement && (
                                <div className="form-group">
                                    <label htmlFor="additionalEmails">Additional Email Recipients</label>
                                    <input
                                        type="text"
                                        id="additionalEmails"
                                        value={milestoneForm.additionalNotifyEmails}
                                        onChange={(e) => setMilestoneForm({...milestoneForm, additionalNotifyEmails: e.target.value})}
                                        placeholder="email1@example.com, email2@example.com"
                                    />
                                    <small className="form-hint">Comma-separated list of additional recipients (Project Manager is automatically included)</small>
                                </div>
                            )}

                            {/* Dynamic Template Fields for Milestone */}
                            {!loadingTemplates && milestoneTemplate && (
                                <div className="template-fields-section">
                                    <div className="template-header">
                                        <FaCog />
                                        <span>{milestoneTemplate.displayName || 'Additional Fields'}</span>
                                    </div>
                                    {getTemplateFields(milestoneTemplate).map(field => (
                                        renderTemplateField(
                                            field,
                                            dynamicFormFields[field.name],
                                            handleDynamicFieldChange
                                        )
                                    ))}
                                </div>
                            )}

                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowMilestoneModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleSaveMilestone}
                                >
                                    {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Column Modal */}
            {showColumnModal && (
                <div className="modal-backdrop" onClick={() => setShowColumnModal(false)}>
                    <div className="modal-content column-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {editingColumn ? 'Edit Column' : 'Create Column'}
                            </h3>
                            <button className="close-btn" onClick={() => setShowColumnModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="columnName">Column Name *</label>
                                <input
                                    type="text"
                                    id="columnName"
                                    value={columnForm.name}
                                    onChange={(e) => setColumnForm({...columnForm, name: e.target.value})}
                                    required
                                    placeholder="e.g., To Do, In Review, Done"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="columnStatusValue">Status Value</label>
                                <input
                                    type="text"
                                    id="columnStatusValue"
                                    value={columnForm.statusValue}
                                    onChange={(e) => setColumnForm({...columnForm, statusValue: e.target.value})}
                                    placeholder="Leave empty to use column name"
                                />
                                <small className="form-hint">The status value assigned to tasks in this column</small>
                            </div>

                            <div className="form-row two-col">
                                <div className="form-group">
                                    <label htmlFor="columnColor">Color</label>
                                    <div className="color-picker">
                                        <input
                                            type="color"
                                            id="columnColor"
                                            value={columnForm.color}
                                            onChange={(e) => setColumnForm({...columnForm, color: e.target.value})}
                                        />
                                        <div className="color-presets">
                                            {['#f39c12', '#3498db', '#9b59b6', '#27ae60', '#e74c3c', '#1abc9c', '#34495e'].map(color => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    className={`color-preset ${columnForm.color === color ? 'selected' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => setColumnForm({...columnForm, color})}
                                                    aria-label={`Select color ${color}`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="columnWipLimit">WIP Limit</label>
                                    <input
                                        type="number"
                                        id="columnWipLimit"
                                        value={columnForm.wipLimit}
                                        onChange={(e) => setColumnForm({...columnForm, wipLimit: e.target.value})}
                                        min="0"
                                        placeholder="0 = no limit"
                                    />
                                    <small className="form-hint">Max tasks allowed in column (0 = unlimited)</small>
                                </div>
                            </div>

                            <div className="form-check-group">
                                <label className="form-check">
                                    <input
                                        type="checkbox"
                                        checked={columnForm.isCompletedColumn}
                                        onChange={(e) => setColumnForm({...columnForm, isCompletedColumn: e.target.checked})}
                                    />
                                    <span>Tasks in this column are considered "completed"</span>
                                </label>
                            </div>

                            <div className="modal-actions">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowColumnModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={handleSaveColumn}
                                >
                                    {editingColumn ? 'Update Column' : 'Create Column'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Detail Modal */}
            {showTaskDetailModal && selectedTask && (
                <div className="modal-backdrop" onClick={handleCloseTaskDetail}>
                    <div className="modal-content task-detail-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                <FaTasks /> {selectedTask.title || selectedTask.description || 'Task Details'}
                            </h3>
                            <button className="close-btn" onClick={handleCloseTaskDetail}>
                                <FaTimes />
                            </button>
                        </div>
                        
                        {/* Tab Navigation */}
                        <div className="task-detail-tabs">
                            <button 
                                className={`tab-btn ${taskDetailTab === 'details' ? 'active' : ''}`}
                                onClick={() => setTaskDetailTab('details')}
                            >
                                <FaInfoCircle /> Details
                            </button>
                            <button 
                                className={`tab-btn ${taskDetailTab === 'subtasks' ? 'active' : ''}`}
                                onClick={() => setTaskDetailTab('subtasks')}
                            >
                                <FaSitemap /> Subtasks
                                {getSubtasks(selectedTask.id).length > 0 && (
                                    <span className="tab-badge">{getSubtasks(selectedTask.id).length}</span>
                                )}
                            </button>
                            <button 
                                className={`tab-btn ${taskDetailTab === 'comments' ? 'active' : ''}`}
                                onClick={() => setTaskDetailTab('comments')}
                            >
                                <FaComments /> Comments
                                {taskComments.length > 0 && (
                                    <span className="tab-badge">{taskComments.length}</span>
                                )}
                            </button>
                            <button 
                                className={`tab-btn ${taskDetailTab === 'files' ? 'active' : ''}`}
                                onClick={() => setTaskDetailTab('files')}
                            >
                                <FaPaperclip /> Files
                                {taskFiles.length > 0 && (
                                    <span className="tab-badge">{taskFiles.length}</span>
                                )}
                            </button>
                            <button 
                                className={`tab-btn ${taskDetailTab === 'checklist' ? 'active' : ''}`}
                                onClick={() => setTaskDetailTab('checklist')}
                            >
                                <FaCheckSquare /> Checklist
                                {taskChecklist.length > 0 && (
                                    <span className="tab-badge">{taskChecklist.filter(i => i.completed).length}/{taskChecklist.length}</span>
                                )}
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            {/* Details Tab */}
                            {taskDetailTab === 'details' && (
                                <div className="task-details-content">
                                    <div className="detail-row">
                                        <span className="detail-label">Status:</span>
                                        <span className={`status-badge status-${selectedTask.status?.toLowerCase().replace(' ', '-')}`}>
                                            {selectedTask.status}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Priority:</span>
                                        <span className={`priority-badge priority-${selectedTask.priority?.toLowerCase()}`}>
                                            {selectedTask.priority}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Assigned To:</span>
                                        <span>{getUserName(selectedTask.assignedToUserID)}</span>
                                    </div>
                                    {selectedTask.department && (
                                        <div className="detail-row">
                                            <span className="detail-label">Department:</span>
                                            <span>{selectedTask.department}</span>
                                        </div>
                                    )}
                                    {selectedTask.milestoneId && (
                                        <div className="detail-row">
                                            <span className="detail-label">Milestone:</span>
                                            <span className="detail-milestone">
                                                <FaFlag style={{ color: milestones.find(m => m.milestoneId === selectedTask.milestoneId)?.color || '#f39c12', fontSize: '0.8rem' }} />
                                                {milestones.find(m => m.milestoneId === selectedTask.milestoneId)?.name || 'Unknown'}
                                            </span>
                                        </div>
                                    )}
                                    {selectedTask.description && (
                                        <div className="detail-row full-width">
                                            <span className="detail-label">Description:</span>
                                            <p className="detail-description">{selectedTask.description}</p>
                                        </div>
                                    )}
                                    <div className="detail-row">
                                        <span className="detail-label">Start Date:</span>
                                        <span>{formatDate(selectedTask.startDate) || 'Not set'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="detail-label">Due Date:</span>
                                        <span className={new Date(selectedTask.expectedCompletionDate) < new Date() && selectedTask.status !== 'Closed' && selectedTask.status !== 'Completed' ? 'overdue' : ''}>
                                            {formatDate(selectedTask.expectedCompletionDate) || 'Not set'}
                                        </span>
                                    </div>
                                    {selectedTask.createdDate && (
                                        <div className="detail-row">
                                            <span className="detail-label">Created:</span>
                                            <span className="detail-date-light">{formatDate(selectedTask.createdDate)}</span>
                                        </div>
                                    )}
                                    {selectedTask.modifiedDate && (
                                        <div className="detail-row">
                                            <span className="detail-label">Last Modified:</span>
                                            <span className="detail-date-light">{formatDate(selectedTask.modifiedDate)}</span>
                                        </div>
                                    )}
                                    {/* Subtask progress summary */}
                                    {getSubtasks(selectedTask.id).length > 0 && (() => {
                                        const subtasks = getSubtasks(selectedTask.id);
                                        const completed = subtasks.filter(s => s.status === 'Closed' || s.status === 'Completed').length;
                                        const pct = Math.round((completed / subtasks.length) * 100);
                                        return (
                                            <div className="detail-row">
                                                <span className="detail-label">Subtask Progress:</span>
                                                <div className="detail-progress">
                                                    <div className="detail-progress-bar">
                                                        <div className="detail-progress-fill" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="detail-progress-text">{completed}/{subtasks.length} ({pct}%)</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div className="detail-actions">
                                        <button className="btn btn-primary" onClick={() => { handleCloseTaskDetail(); handleEditTask(selectedTask); }}>
                                            <FaEdit /> Edit Task
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => { handleCloseTaskDetail(); handleCreateTask(selectedTask.id); }}>
                                            <FaPlus /> Add Subtask
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            {/* Subtasks Tab */}
                            {taskDetailTab === 'subtasks' && (
                                <div className="subtasks-content">
                                    <div className="subtasks-header">
                                        <h4>Subtasks</h4>
                                        <button className="btn btn-primary btn-sm" onClick={() => { handleCloseTaskDetail(); handleCreateTask(selectedTask.id); }}>
                                            <FaPlus /> Add Subtask
                                        </button>
                                    </div>
                                    {getSubtasks(selectedTask.id).length === 0 ? (
                                        <div className="empty-state-small">
                                            <FaSitemap />
                                            <p>No subtasks yet</p>
                                        </div>
                                    ) : (
                                        <div className="subtasks-list-modal">
                                            {getSubtasks(selectedTask.id).map(subtask => (
                                                <div 
                                                    key={subtask.id} 
                                                    className={`subtask-item-modal ${subtask.status === 'Closed' || subtask.status === 'Completed' ? 'completed' : ''}`}
                                                    onClick={() => { setSelectedTask(subtask); setTaskDetailTab('details'); }}
                                                >
                                                    <FaGripVertical className="drag-handle" />
                                                    {subtask.status === 'Closed' || subtask.status === 'Completed' ? <FaCheckCircle /> : <FaCircle />}
                                                    <span className="subtask-title">{subtask.title || subtask.description}</span>
                                                    <span className={`status-badge-sm status-${subtask.status?.toLowerCase().replace(' ', '-')}`}>
                                                        {subtask.status}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Comments Tab */}
                            {taskDetailTab === 'comments' && (
                                <div className="comments-content">
                                    <CommentInputSection
                                        commentText={newComment}
                                        onCommentChange={(e) => setNewComment(e.target.value)}
                                        onCommentSubmit={handleAddComment}
                                        isSubmitting={isSubmittingComment}
                                        allUsers={users}
                                        getUserDisplay={getUserDisplay}
                                    />
                                    <div className="comments-list">
                                        {taskComments.length === 0 ? (
                                            <div className="empty-state-small">
                                                <FaComments />
                                                <p>No comments yet</p>
                                            </div>
                                        ) : (
                                            taskComments.map((comment, idx) => {
                                                const commentUser = users.find(u => (u.userID ?? u.id) === comment.changedByUserId);
                                                const commentBody = comment.comment || comment.newValue;
                                                return (
                                                    <div key={idx} className="comment-item">
                                                        <div className="comment-header">
                                                            <span className="comment-author">
                                                                <FaUserCircle /> {commentUser ? getUserDisplay(commentUser) : `User #${comment.changedByUserId}`}
                                                            </span>
                                                            <span className="comment-date">
                                                                {formatDate(comment.changedAt)}
                                                            </span>
                                                        </div>
                                                        <div className="comment-body">
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {commentBody || ''}
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Files Tab */}
                            {taskDetailTab === 'files' && (
                                <div className="files-content">
                                    <div className="file-upload-form">
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            multiple
                                            style={{ display: 'none' }}
                                        />
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <FaUpload /> Upload File
                                        </button>
                                    </div>
                                    <div className="files-list">
                                        {taskFiles.length === 0 ? (
                                            <div className="empty-state-small">
                                                <FaFolderOpen />
                                                <p>No files attached</p>
                                            </div>
                                        ) : (
                                            taskFiles.map((file, idx) => (
                                                <div key={idx} className="file-item">
                                                    <FaFile />
                                                    <span className="file-name">{file.fileName || file.name}</span>
                                                    <span className="file-size">{file.fileSize ? `${Math.round(file.fileSize / 1024)} KB` : ''}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Checklist Tab */}
                            {taskDetailTab === 'checklist' && (
                                <div className="checklist-content">
                                    <div className="add-checklist-form">
                                        <input
                                            type="text"
                                            placeholder="Add checklist item..."
                                            value={newChecklistItem}
                                            onChange={(e) => setNewChecklistItem(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                                        />
                                        <button 
                                            className="btn btn-primary"
                                            onClick={handleAddChecklistItem}
                                            disabled={!newChecklistItem.trim()}
                                        >
                                            <FaPlus /> Add
                                        </button>
                                    </div>
                                    <div className="checklist-list">
                                        {taskChecklist.length === 0 ? (
                                            <div className="empty-state-small">
                                                <FaCheckSquare />
                                                <p>No checklist items</p>
                                            </div>
                                        ) : (
                                            taskChecklist.map((item, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`checklist-item ${item.completed ? 'completed' : ''}`}
                                                    onClick={() => handleToggleChecklistItem(item.id, item.completed)}
                                                >
                                                    {item.completed ? <FaCheckSquare /> : <FaSquare />}
                                                    <span className="checklist-text">{item.text}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectDetailPage;
